import constants from "../../Constants/constants";
import { authClient, cookiesToDelete, forceLogout } from "./client";
/**
 * @name fetchApi
 * @description will fetch the data based on params supplied
 * @param {string} param
 * @param {string} method
 * @param {object} variables
 */
/**
 * @name fetchStaffApi
 * @description will fetch the data based on params supplied
 * @param {string} param
 * @param {string} method
 * @param {object} variables
 */

/*
 * Session teardown and the axios instance both live in ./client alongside the
 * in-memory access token. Re-exported here so the many existing importers of
 * this module keep working unchanged.
 */
export { cookiesToDelete, forceLogout };

/*
 * Surfaces the error body to the caller only when it carries something
 * readable. The two variants are not interchangeable -- these wrappers differ
 * in which fields they expose, and call sites depend on the exact shape.
 */
const bodyIfMessage = (data) => (data?.message ? data : false);
const bodyIfAnyMessage = (data) => (data?.message || data?.error_message ? data : false);

/*
 * The interceptor matches auth-free endpoints against the bare path, so it is
 * passed separately: config.url carries the environment's base prefix and
 * would never match.
 */
export const fetchApi = (param = null, method = null, variables = null) =>
	authClient({
		method: method,
		url: `${constants.ApiUrl}${param}`,
		data: variables,
		__requestPath: param,
	})
		.then((res) => {
			return res.data;
		})
		.catch((err) => {
			const status = err?.response?.status;
			// No response at all: let the caller's own .catch() handle it.
			if (!status) throw err;
			const data = err.__authBody ?? err?.response?.data;

			let requestPaths = ["/login", "/check-username", "/validate-token", "/get-report"]

			if (status === 401 || status === 400) {
				return bodyIfAnyMessage(data);
			} else if ((status === 403 || status === 429) && requestPaths.includes(param)) {
				return bodyIfAnyMessage(data);
			} else {
				return false;
			}
		});

export const fetchStaffApi = (param = null, method = null, variables = null, type = null) =>
	authClient({
		method: method,
		url: type === 'student' ? `${constants.StudentApiUrl}${param}` : `${constants.StaffApiUrl}${param}`,
		data: variables,
		__requestPath: param,
	})
		.then((res) => {
			return res.data;
		})
		.catch((err) => {
			const status = err?.response?.status;
			if (!status) throw err;
			const data = err.__authBody ?? err?.response?.data;

			let requestPaths = ["/get-mail-details", "/get-faculty-list", "/get-faculty-list-id/", "/get-mail-reports"
				, "mail-type", "/get-all-recipients"
			]
			const isMatchedPath = requestPaths?.some(path =>
				param?.startsWith(path)
			);

			if (status === 401 || status === 400) {
				return bodyIfMessage(data);
			} else if ((status === 403 || status === 429) && isMatchedPath) {
				return bodyIfAnyMessage(data);
			} else {
				return false;
			}
		});

export const fetchStaffFormData = (url = null, method = null, formData = null, type = null) =>
	authClient({
		method,
		url: type === 'student' ? `${constants.StudentApiUrl}${url}` : type === "common" ? `${constants.ApiUrl}${url}` : `${constants.StaffApiUrl}${url}`,
		data: formData,
		headers: {
			'Content-Type': 'multipart/form-data', // Make sure to set the content type for FormData
		},
		__requestPath: url,
	})
		.then((res) => {
			return res.data;
		})
		.catch((err) => {
			const status = err?.response?.status;
			if (!status) throw err;
			const data = err.__authBody ?? err?.response?.data;

			let requestPaths = ["/student-initial-import", "/student-id-import", "/student-asha-psha-import", "/save-student-record"]
			const isMatchedPath = requestPaths.some(path =>
				url?.startsWith(path)
			);

			if (status === 401 || status === 400) {
				return bodyIfMessage(data);
			} else if ((status === 403 || status === 429) && isMatchedPath) {
				return bodyIfAnyMessage(data);
			} else {
				return false;
			}
		});

export const fetchStaffDownloadApi = (param = null, method = null, variables = null) =>
	authClient({
		method: method,
		url: `${constants.StaffApiUrl}${param}`,
		data: variables,
		responseType: 'blob',
		__requestPath: param,
	})
		.then(async (res) => {
			if (res?.headers?.['content-type']?.includes('application/json')) {
				const text = await res?.data.text();
				const data = JSON.parse(text);
				return data
			}
			return res.data;
		})
		.catch((err) => {
			const status = err?.response?.status;
			if (!status) throw err;
			const data = err.__authBody ?? err?.response?.data;

			if (status === 401 || status === 400) {
				return bodyIfMessage(data);
			} else {
				return false;
			}
		});

export const fetchDownloadApi = (param = null, method = null, variables = null) =>
	authClient({
		method: method,
		url: `${constants.ApiUrl}${param}`,
		data: variables,
		responseType: 'blob',
		__requestPath: param,
	})
		.then(async (res) => {
			if (res?.headers?.['content-type']?.includes('application/json')) {
				const text = await res?.data.text();
				const data = JSON.parse(text);
				return data
			}
			return res.data;
		})
		.catch((err) => {
			const status = err?.response?.status;
			if (!status) throw err;
			const data = err.__authBody ?? err?.response?.data;

			if (status === 401 || status === 400) {
				return bodyIfMessage(data);
			} else {
				return false;
			}
		});
